import { AppButton, ConfirmationModal } from "@libs/zapi-ui/custom";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";
import { SparkleIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import { startGeneration, useClaimsStoreSelector, selectIsGenerating } from "../model/store";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

type TProps = {
  children: ReactNode;
  projectId: string;
  isWithWarning?: boolean;
  onComplete: () => Promise<void>;
  isDisabled?: boolean;
  chapterId: string | undefined;
  isChapterContentEmpty: boolean;
};

export function AnalyzeClaims({
  children,
  projectId,
  isWithWarning,
  isDisabled,
  onComplete,
  chapterId,
  isChapterContentEmpty
}: TProps) {
  const [isWarningModalOpen, setIsWarningModalOpen] = useState(false);
  const { role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const isGenerating = useClaimsStoreSelector(selectIsGenerating(chapterId));

  const handleGenerate = () => {
    if (!chapterId || isGenerating) return;

    if (isWithWarning) {
      setIsWarningModalOpen(true);
      return;
    }
    startGeneration(Number(projectId), Number(chapterId), onComplete);
  };

  const handleWarningConfirm = () => {
    setIsWarningModalOpen(false);
    startGeneration(Number(projectId), Number(chapterId), onComplete);
  };

  const handleWarningCancel = () => {
    setIsWarningModalOpen(false);
  };

  if (!hasEdit) return null;

  return (
    <>
      <AppButton
        startAdornment={<SparkleIcon className="w-4 h-4" />}
        size="sm"
        appearance="contained"
        onClick={handleGenerate}
        data-testid="regenerate-claims-button"
        isLoading={isGenerating}
        disabled={isGenerating || isChapterContentEmpty || isDisabled}
      >
        {children}
      </AppButton>

      <ConfirmationModal
        isOpen={isWarningModalOpen}
        onClose={handleWarningCancel}
        onConfirm={handleWarningConfirm}
        title="Regenerate Claims Analysis"
        description="This action will delete all existing claims data and generate new analysis. This cannot be undone. Are you sure you want to continue?"
        confirmText="Yes, Regenerate"
        cancelText="Cancel"
      />
    </>
  );
}
